import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ClientInvoiceLinkModal — relier une facture (module Finance) à un
// projet GestionPro directement depuis la fiche client.
// On ne propose que les factures du commerce qui ne sont encore
// rattachées à aucun projet ; celles du client concerné remontent en
// premier (même email, même téléphone ou même nom).
// ─────────────────────────────────────────────────────────────

export interface LinkableInvoice {
  id: number;
  number: string | null;
  customer_name: string | null;
  customer_email: string | null;
  customer_phone: string | null;
  total: number | null;
  amount_paid: number | null;
  status: string | null;
  currency: string | null;
  issue_date: string | null;
  doc_type: string | null;
}

interface Props {
  commerceId: number | null;
  projectId: number;
  projectTitle: string;
  client: { name?: string; email?: string; phone?: string };
  onClose: () => void;
  onLinked: () => void;
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function normalize(value: string | null | undefined): string {
  return String(value || '').trim().toLowerCase();
}

function digits(value: string | null | undefined): string {
  return String(value || '').replace(/\D/g, '');
}

export default function ClientInvoiceLinkModal({
  commerceId,
  projectId,
  projectTitle,
  client,
  onClose,
  onLinked,
}: Props) {
  const [invoices, setInvoices] = useState<LinkableInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [linking, setLinking] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    if (!commerceId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('finance_invoices')
        .select('id, number, customer_name, customer_email, customer_phone, total, amount_paid, status, currency, issue_date, doc_type, project_id')
        .eq('merchant_id', commerceId)
        .is('project_id', null)
        .neq('doc_type', 'quote')
        .order('created_at', { ascending: false })
        .limit(200);
      if (err) throw err;
      setInvoices((data || []) as LinkableInvoice[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger les factures.');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [commerceId]);

  useEffect(() => { void load(); }, [load]);

  const matchesClient = useCallback((inv: LinkableInvoice): boolean => {
    const clientEmail = normalize(client.email);
    const clientName = normalize(client.name);
    const clientPhone = digits(client.phone).slice(-9);
    if (clientEmail && normalize(inv.customer_email) === clientEmail) return true;
    const invPhone = digits(inv.customer_phone);
    if (clientPhone && invPhone && invPhone.endsWith(clientPhone)) return true;
    if (clientName && normalize(inv.customer_name).includes(clientName)) return true;
    return false;
  }, [client.email, client.name, client.phone]);

  const visible = useMemo(() => {
    const q = normalize(search);
    const list = invoices.filter((inv) => {
      if (!q) return true;
      return normalize(inv.number).includes(q)
        || normalize(inv.customer_name).includes(q)
        || normalize(inv.customer_email).includes(q);
    });
    return list
      .map((inv) => ({ inv, match: matchesClient(inv) }))
      .sort((a, b) => Number(b.match) - Number(a.match))
      .map((x) => x.inv);
  }, [invoices, search, matchesClient]);

  const link = async (invoiceId: number) => {
    setLinking(invoiceId);
    setError(null);
    try {
      const { error: err } = await supabase
        .from('finance_invoices')
        .update({ project_id: projectId, updated_at: new Date().toISOString() })
        .eq('id', invoiceId);
      if (err) throw err;
      onLinked();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Le rattachement a échoué.');
    } finally {
      setLinking(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={onClose}>
      <div
        className="w-full sm:max-w-xl max-h-[88dvh] flex flex-col bg-background-50 rounded-t-2xl sm:rounded-xl border border-background-200/70 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-background-200/70">
          <div className="min-w-0">
            <p className="text-sm font-bold text-foreground-950 flex items-center gap-1.5">
              <i className="ri-link-m text-primary-600"></i>
              Relier une facture
            </p>
            <p className="text-[11px] text-foreground-500 truncate">Projet : {projectTitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-400 hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="px-4 py-2.5 border-b border-background-200/70">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un numéro, un client…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-14">
              <i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i>
            </div>
          ) : error ? (
            <div className="p-4 text-center">
              <p className="text-xs text-red-600 mb-2">{error}</p>
              <button
                type="button"
                onClick={() => void load()}
                className="px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-xs font-medium cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                Réessayer
              </button>
            </div>
          ) : visible.length === 0 ? (
            <div className="py-14 px-6 text-center">
              <i className="ri-file-list-3-line text-2xl text-foreground-300 block mb-2"></i>
              <p className="text-sm text-foreground-600">Aucune facture disponible.</p>
              <p className="text-[11px] text-foreground-400 mt-1">
                Les factures déjà rattachées à un projet ne sont pas proposées.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-background-200/70">
              {visible.map((inv) => {
                const total = num(inv.total);
                const paid = num(inv.amount_paid);
                const rest = Math.max(0, total - paid);
                const isClient = matchesClient(inv);
                return (
                  <div key={inv.id} className="flex items-center gap-3 px-4 py-3">
                    <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isClient ? 'bg-primary-100 text-primary-700' : 'bg-background-100 text-foreground-500'}`}>
                      <i className="ri-file-text-line"></i>
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground-900 truncate">
                        {inv.number || `Facture #${inv.id}`}
                        {isClient && (
                          <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-800 align-middle">ce client</span>
                        )}
                      </p>
                      <p className="text-[11px] text-foreground-500 truncate">
                        {inv.customer_name || 'Client non renseigné'}
                        {inv.issue_date ? ` · ${new Date(inv.issue_date).toLocaleDateString('fr-FR')}` : ''}
                      </p>
                      <p className="text-[11px] text-foreground-400">
                        Total {total.toLocaleString('fr-FR')} {inv.currency || 'MAD'}
                        {rest > 0 ? ` · reste ${rest.toLocaleString('fr-FR')}` : ' · soldée'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => void link(inv.id)}
                      disabled={linking === inv.id}
                      className="px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
                    >
                      {linking === inv.id ? <i className="ri-loader-4-line animate-spin"></i> : 'Relier'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}